import { useCallback, useState } from 'react';
import type { ScanResult } from '../engine/types';
import { getProbeCount } from '../engine/registry';
import { JSON_SCHEMA } from '../../shared/jsonDoc';

/**
 * The export payload for a completed scan.
 *
 * Deliberately shaped like the /json document so the two are comparable: a
 * user who runs `curl /json` and then exports from the page can diff the two
 * documents and see exactly which half came from where.
 *
 * The browser section is filled in here because this code IS running in a
 * browser. That is the whole point: the /json endpoint cannot report the
 * browser half, and this is the honest way to obtain it.
 */
function buildExport(result: ScanResult) {
  // The edge probe already collected real transport data during the scan, so
  // the export reuses those measured values rather than re-fetching. If the
  // edge was unavailable the signals report status 'unavailable' and the field
  // stays null, which is the honest representation.
  const edge = result.signals['edge'] ?? {};
  const val = (name: string): unknown => edge[name]?.value ?? null;

  return {
    schema: JSON_SCHEMA,
    generatedAt: Date.now(),
    transport: {
      available: val('available') === true,
      tlsVersion: val('tlsVersion') as string | null,
      tlsCipher: val('tlsCipher') as string | null,
      tlsClientHelloLength: val('tlsClientHelloLength') as string | number | null,
      tlsClientCiphersSha1: val('tlsClientCiphersSha1') as string | null,
      tlsClientExtensionsSha1: val('tlsClientExtensionsSha1') as string | null,
      httpProtocol: val('httpProtocol') as string | null,
      clientTcpRtt: val('clientTcpRtt') as number | null,
      clientQuicRtt: val('clientQuicRtt') as number | null,
      asOrganization: val('asOrganization') as string | null,
      colo: val('colo') as string | null,
      botManagementEnabled: val('botManagementEnabled') === true,
      ja4: val('ja4') as string | null,
      ja3Hash: val('ja3Hash') as string | null,
    },
    browser: {
      available: true,
      collected: {
        status: result.status,
        fingerprintHash: result.fingerprintHash,
        fingerprintId: result.fingerprintId,
        fingerprintParts: result.fingerprintParts,
        botScore: result.botScore,
        reasons: result.reasons,
        crossContext: result.crossContext,
        signals: result.signals,
        findings: result.findings,
        startedAt: result.startedAt,
        finishedAt: result.finishedAt,
      },
    },
    catalog: { probes: getProbeCount() },
  };
}

function filename(result: ScanResult): string {
  // Short hash keeps the file identifiable without leaking the full ID.
  return `fingerprint-lab-${result.fingerprintHash.slice(0, 12) || 'scan'}.json`;
}

export function ExportButtons({ result }: { result: ScanResult | null }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const download = useCallback(() => {
    if (!result) return;
    const text = JSON.stringify(buildExport(result), null, 2);
    const blob = new Blob([text], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename(result);
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke on the next tick: revoking synchronously can cancel the download
    // in some browsers before it starts reading the blob.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }, [result]);

  const copy = useCallback(async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(buildExport(result), null, 2));
      setState('copied');
      setTimeout(() => setState('idle'), 2000);
    } catch {
      // Clipboard access is denied in some contexts (insecure origin, no
      // permission). Falling back to a download is more useful than an error.
      setState('failed');
      download();
      setTimeout(() => setState('idle'), 2000);
    }
  }, [result, download]);

  const btn =
    'rounded-lg border border-orange-500/25 bg-orange-500/10 px-3 py-1.5 text-sm font-medium text-orange-200 transition-colors hover:bg-orange-500/20 disabled:opacity-40';

  return (
    <div className="flex items-center gap-2">
      <button type="button" className={btn} onClick={download} disabled={!result}>
        Export JSON
      </button>
      <button type="button" className={btn} onClick={() => void copy()} disabled={!result}>
        {state === 'copied' ? 'Copied' : state === 'failed' ? 'Downloaded' : 'Copy JSON'}
      </button>
    </div>
  );
}
